import { useGoogleLogin } from "@react-oauth/google";
import { useSetAtom } from "jotai";
import { useState } from "react";
import { toast } from "react-toastify";
import { assertEngineEarsErrorType } from "../../api/helpers";
import { currentFlowAtom } from "../../atoms/location/currentFlowAtom";
import { AuthFlows } from "../../constants/authSteps";
import { TRACKING_EVENTS_NAME } from "../../constants/trackingEvents";
import { useAppSelector } from "../../store/hooks";
import { emitAnalyticsTrackingEvent } from "../../utils/analyticsUtils";
import { useCheckUserLoginFlow } from "./useCheckUserLoginFlow";
import useLoginGoogleMutation from "./useLoginGoogleMutation";

const useGoogleAuth = () => {
  const setFlow = useSetAtom(currentFlowAtom);
  const [isGoogleLoginOpen, setIsGoogleLoginOpen] = useState(false);
  const { checkUserAndSetFlow } = useCheckUserLoginFlow();
  const { mutateAsync: loginGoogle } = useLoginGoogleMutation();
  const localUTMParams = useAppSelector(
    (state) => state.accountInfo.localUTMParams,
  );

  const handleGoogleLogin = useGoogleLogin({
    onError: () => {
      toast.error(
        "Something went wrong. Please try again or reach out to customer support.",
      );
      setIsGoogleLoginOpen(false);
    },
    onNonOAuthError: () => {
      setIsGoogleLoginOpen(false);
    },
    onSuccess: async (codeResponse) => {
      const { code } = codeResponse;
      try {
        const result = await loginGoogle({ authorization_code: code });
        if (!assertEngineEarsErrorType(result)) {
          emitAnalyticsTrackingEvent("login_google", {});
          if (result.created) {
            emitAnalyticsTrackingEvent(
              TRACKING_EVENTS_NAME.REGISTER_VIA_GOOGLE,
              {
                email: result.user.email,
                phoneNumber: result.user.phone_number?.phone_number,
                userId: result.user.id,
                username: result.user.username,
                ...localUTMParams,
              },
            );
          }
          if (result.created) {
            if (
              result.user.is_valid_aandr_email === true &&
              result.user.is_umg_whitelisted_email === false
            ) {
              setFlow(AuthFlows.AANDR_NON_UMG);
            } else {
              setFlow(AuthFlows.STANDARD_SIGNUP);
            }
          } else {
            checkUserAndSetFlow(result.user);
          }
          setIsGoogleLoginOpen(false);
        }
      } catch {
        /* empty */
        setIsGoogleLoginOpen(false);
      }
    },
    flow: "auth-code",
  });

  const handleGoogleClick = () => {
    emitAnalyticsTrackingEvent("auth_click_on_google_login", {});
    handleGoogleLogin();
    setIsGoogleLoginOpen(true);
  };
  return { handleGoogleClick, isGoogleLoginOpen };
};

export default useGoogleAuth;
